import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from './ui/sheet';
import { Slider } from './ui/slider';
import { useT } from '../i18n/I18nContext';

export default function FilterPanel({ filters, onChange, onApply }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  const handleApply = () => {
    onChange(localFilters);
    onApply();
    setOpen(false);
  };

  const handleReset = () => {
    const resetFilters = {
      distanza_max: null,
      prezzo_min: null,
      prezzo_max: null,
      disponibile: false,
    };
    setLocalFilters(resetFilters);
    onChange(resetFilters);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== null && v !== false).length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="relative rounded-xl border-slate-200 bg-white"
          data-testid="filter-button"
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" />
          {t('filters.label')}
          {activeFiltersCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="bottom" 
        className="rounded-t-3xl bg-white border-t border-slate-200"
        data-testid="filter-panel"
      >
        <SheetHeader className="pb-4">
          <SheetTitle className="font-outfit text-xl text-slate-900">{t('filters.title')}</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 py-4 bg-white">
          {/* Distance slider */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-800">
              {t('filters.max_distance')}
            </Label>
            <div className="px-1">
              <Slider
                value={[localFilters.distanza_max || 50]}
                onValueChange={([value]) => setLocalFilters({...localFilters, distanza_max: value})}
                min={1}
                max={50}
                step={1}
                className="w-full"
                data-testid="distance-slider"
              />
            </div>
            <p className="text-sm font-medium text-slate-600">
              {localFilters.distanza_max ? t('filters.within_km', { km: localFilters.distanza_max }) : t('filters.no_limit')}
            </p>
          </div>

          {/* Price range */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-800">
              {t('filters.price_range')}
            </Label>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder={t('filters.min_price')}
                  value={localFilters.prezzo_min || ''}
                  onChange={(e) => setLocalFilters({
                    ...localFilters,
                    prezzo_min: e.target.value ? parseFloat(e.target.value) : null
                  })}
                  className="rounded-xl bg-white border-slate-300 text-slate-800"
                  data-testid="price-min-input"
                />
              </div>
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder={t('filters.max_price')}
                  value={localFilters.prezzo_max || ''}
                  onChange={(e) => setLocalFilters({
                    ...localFilters,
                    prezzo_max: e.target.value ? parseFloat(e.target.value) : null
                  })}
                  className="rounded-xl bg-white border-slate-300 text-slate-800"
                  data-testid="price-max-input"
                />
              </div>
            </div>
          </div>

          {/* Availability toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
            <Label className="text-sm font-semibold text-slate-800">
              {t('filters.available_only')}
            </Label>
            <Switch
              checked={localFilters.disponibile}
              onCheckedChange={(checked) => setLocalFilters({...localFilters, disponibile: checked})}
              data-testid="availability-switch"
            />
          </div>
        </div>

        <SheetFooter className="flex gap-3 pt-4 pb-6">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex-1 rounded-xl border-slate-300 text-slate-700"
            data-testid="reset-filters-button"
          >
            {t('filters.reset')}
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            data-testid="apply-filters-button"
          >
            {t('filters.apply')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
